import React, { useState, useMemo } from 'react';
import {
  CheckCircle,
  AlertCircle,
  XCircle,
  Minus,
  ChevronDown,
  ChevronRight,
  Upload,
  Check,
  FileText,
  Clock
} from 'lucide-react';
import {
  EVIDENCE_CHECKS,
  EVIDENCE_STATUS,
  CHECK_TYPES,
  getEvidenceChecksForLevel
} from '../../config/cisaEvidenceModel';
import './EvidenceCheckTable.css';

/**
 * EvidenceCheckTable - Per-function verification table showing evidence checks
 *
 * Displays a table of evidence checks for a specific function/level with:
 * - Status dropdown (Verified/Partial/Missing/N/A)
 * - Evidence upload button
 * - Expiration warnings
 * - Bulk verify option
 */
function EvidenceCheckTable({
  functionCode,
  functionName,
  currentLevel,
  evidenceChecks = {},
  onUpdateCheck,
  onUploadEvidence,
  onBulkVerify
}) {
  const [expandedLevel, setExpandedLevel] = useState(currentLevel);
  const [selectedChecks, setSelectedChecks] = useState(new Set());

  // Get all levels up to and including current level
  const levels = useMemo(() => {
    const allLevels = ['INITIAL', 'ADVANCED', 'OPTIMAL'];
    const currentIdx = allLevels.indexOf(currentLevel);
    if (currentIdx === -1) return [];
    return allLevels.slice(0, currentIdx + 1);
  }, [currentLevel]);

  // Get checks for each level
  const checksByLevel = useMemo(() => {
    const result = {};
    for (const level of levels) {
      result[level] = getEvidenceChecksForLevel(functionCode, level);
    }
    return result;
  }, [functionCode, levels]);

  // Calculate verification stats
  const stats = useMemo(() => {
    let totalRequired = 0;
    let verifiedRequired = 0;
    let totalAll = 0;
    let verifiedAll = 0;

    for (const level of levels) {
      const checks = checksByLevel[level] || [];
      for (const check of checks) {
        totalAll++;
        const status = evidenceChecks[check.id]?.status;
        if (status === 'VERIFIED') verifiedAll++;
        if (check.type === 'REQUIRED') {
          totalRequired++;
          if (status === 'VERIFIED') verifiedRequired++;
        }
      }
    }

    return {
      totalRequired,
      verifiedRequired,
      totalAll,
      verifiedAll,
      requiredPercentage: totalRequired > 0 ? Math.round((verifiedRequired / totalRequired) * 100) : 100
    };
  }, [levels, checksByLevel, evidenceChecks]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'VERIFIED':
        return <CheckCircle className="ecr-status-icon ecr-status-verified" />;
      case 'PARTIAL':
        return <AlertCircle className="ecr-status-icon ecr-status-partial" />;
      case 'MISSING':
        return <XCircle className="ecr-status-icon ecr-status-missing" />;
      case 'NOT_APPLICABLE':
        return <Minus className="ecr-status-icon ecr-status-na" />;
      default:
        return <XCircle className="ecr-status-icon ecr-status-missing" />;
    }
  };

  const handleStatusChange = (checkId, newStatus) => {
    onUpdateCheck?.(checkId, {
      status: newStatus,
      verifiedBy: newStatus === 'VERIFIED' ? 'User' : null,
      notes: ''
    });
  };

  const handleToggleSelect = (checkId) => {
    setSelectedChecks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(checkId)) {
        newSet.delete(checkId);
      } else {
        newSet.add(checkId);
      }
      return newSet;
    });
  };

  const handleSelectAllForLevel = (level) => {
    const checks = checksByLevel[level] || [];
    const allSelected = checks.every(c => selectedChecks.has(c.id));

    setSelectedChecks(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        checks.forEach(c => newSet.delete(c.id));
      } else {
        checks.forEach(c => newSet.add(c.id));
      }
      return newSet;
    });
  };

  const handleBulkVerify = () => {
    if (selectedChecks.size === 0) return;
    const updates = {};
    selectedChecks.forEach(checkId => {
      updates[checkId] = {
        checkId,
        status: 'VERIFIED',
        verifiedAt: new Date().toISOString(),
        verifiedBy: 'User',
        notes: ''
      };
    });
    onBulkVerify?.(updates);
    setSelectedChecks(new Set());
  };

  if (currentLevel === 'TRADITIONAL' || levels.length === 0) {
    return (
      <div className="ecr-container ecr-empty">
        <FileText className="ecr-empty-icon" />
        <p className="ecr-empty-text">
          Evidence checks are only required at Initial level and above.
        </p>
      </div>
    );
  }

  return (
    <div className="ecr-container">
      {/* Summary Header */}
      <div className="ecr-header">
        <div className="ecr-header-title">
          <h4>Evidence Verification</h4>
          <span className="ecr-header-subtitle">{functionName}</span>
        </div>
        <div className="ecr-header-stats">
          <div className="ecr-stat">
            <span className="ecr-stat-value">{stats.verifiedRequired}</span>
            <span className="ecr-stat-label">/ {stats.totalRequired} Required</span>
          </div>
          <div className={`ecr-stat-badge ${stats.requiredPercentage === 100 ? 'ecr-complete' : ''}`}>
            {stats.requiredPercentage}%
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedChecks.size > 0 && (
        <div className="ecr-bulk-actions">
          <span className="ecr-bulk-count">{selectedChecks.size} selected</span>
          <button
            className="ecr-bulk-btn ecr-bulk-verify"
            onClick={handleBulkVerify}
          >
            <Check size={14} />
            Verify Selected
          </button>
          <button
            className="ecr-bulk-btn ecr-bulk-clear"
            onClick={() => setSelectedChecks(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {/* Checks by Level */}
      <div className="ecr-levels">
        {levels.map(level => {
          const levelChecks = checksByLevel[level] || [];
          const isExpanded = expandedLevel === level;
          const verifiedCount = levelChecks.filter(
            c => evidenceChecks[c.id]?.status === 'VERIFIED'
          ).length;

          return (
            <div key={level} className="ecr-level-section">
              <button
                className="ecr-level-header"
                onClick={() => setExpandedLevel(isExpanded ? null : level)}
              >
                <div className="ecr-level-title">
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className={`ecr-level-badge ecr-level-${level.toLowerCase()}`}>
                    {level}
                  </span>
                  <span className="ecr-level-progress">
                    {verifiedCount} / {levelChecks.length} verified
                  </span>
                </div>
                <div className="ecr-level-actions">
                  {levelChecks.length > 0 && (
                    <span
                      className="ecr-select-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAllForLevel(level);
                      }}
                    >
                      {levelChecks.every(c => selectedChecks.has(c.id)) ? 'Deselect All' : 'Select All'}
                    </span>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="ecr-checks-list">
                  {levelChecks.length === 0 ? (
                    <div className="ecr-no-checks">No checks defined for this level</div>
                  ) : (
                    levelChecks.map(check => {
                      const checkStatus = evidenceChecks[check.id] || {};
                      const status = checkStatus.status || 'MISSING';
                      const isSelected = selectedChecks.has(check.id);

                      return (
                        <div
                          key={check.id}
                          className={`ecr-check-row ${isSelected ? 'ecr-check-selected' : ''}`}
                        >
                          <div className="ecr-check-select">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelect(check.id)}
                            />
                          </div>
                          <div className="ecr-check-status">
                            {getStatusIcon(status)}
                          </div>
                          <div className="ecr-check-content">
                            <div className="ecr-check-question">
                              {check.question}
                              {check.type === 'REQUIRED' && (
                                <span className="ecr-check-required">Required</span>
                              )}
                              {check.type === 'RECOMMENDED' && (
                                <span className="ecr-check-recommended">Recommended</span>
                              )}
                            </div>
                            <div className="ecr-check-meta">
                              <span className="ecr-check-source">
                                <FileText size={12} />
                                {check.source}
                              </span>
                              {check.productHint && (
                                <span className="ecr-check-product">
                                  {check.productHint}
                                </span>
                              )}
                            </div>
                            {checkStatus.verifiedAt && (
                              <div className="ecr-check-verified-info">
                                <Clock size={12} />
                                Verified {new Date(checkStatus.verifiedAt).toLocaleDateString()}
                                {checkStatus.verifiedBy && ` by ${checkStatus.verifiedBy}`}
                              </div>
                            )}
                          </div>
                          <div className="ecr-check-actions">
                            <select
                              className="ecr-status-select"
                              value={status}
                              onChange={(e) => handleStatusChange(check.id, e.target.value)}
                            >
                              <option value="MISSING">Missing</option>
                              <option value="PARTIAL">Partial</option>
                              <option value="VERIFIED">Verified</option>
                              <option value="NOT_APPLICABLE">N/A</option>
                            </select>
                            <button
                              className="ecr-upload-btn"
                              onClick={() => onUploadEvidence?.(check)}
                              title="Upload evidence"
                            >
                              <Upload size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EvidenceCheckTable;
